import React, { useState } from "react";
import { motion } from "framer-motion";
import { Form, Input, Label, Spinner } from "reactstrap";
import { CheckCircle2, CheckCircle, ShieldCheck } from "lucide-react";
import { triggerToast } from "../../../utils/toast";
import { submitSidequestPreRegistration } from "../../../services/sidequestApi";
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics";
import styles from "../sidequest.module.scss";

const registerBullets = [
  "Free to apply, no commitment required",
  "Priority verification for Sidequest developers",
  "Access granted directly to your plan once approved",
];

const initialFormData = {
  name: "",
  email: "",
  companyName: "",
  socialMediaLink: "",
  message: "",
};

// A real name is letters (plus accented characters) optionally joined by a
// single space/hyphen/apostrophe between words — rejects "..." , "N/A",
// "asdf123", or a lone symbol, which is what was slipping through before and
// bouncing back as a server-side error instead of an inline one.
const NAME_REGEX = /^[A-Za-zÀ-ɏ]+(?:[\s'-][A-Za-zÀ-ɏ]+)*$/;

// Standard practical email pattern (same shape browsers use for
// `type="email"` validation) — catches the missing "@"/domain/TLD cases
// that were reaching the backend before.
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const validateField = (id, rawValue) => {
  const value = rawValue.trim();
  switch (id) {
    case "name":
      if (!value) return "Please enter your name.";
      if (value.length < 2) return "Name is too short.";
      if (value.length > 80) return "Name is too long.";
      if (!NAME_REGEX.test(value)) {
        return "Please enter your real name (letters only — no numbers or symbols).";
      }
      return "";
    case "email":
      if (!value) return "Please enter your work email.";
      if (!EMAIL_REGEX.test(value)) return "Please enter a valid email address.";
      return "";
    case "socialMediaLink":
      if (!value) return ""; // optional
      if (/\s/.test(value)) return "Link can't contain spaces.";
      if (value.length < 3) return "Please enter a valid link or handle.";
      return "";
    default:
      return "";
  }
};

const RegisterForm = () => {
  const { trackEvent } = useAnalytics();
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    // Re-validate live once a field has already been flagged, so the error
    // clears the moment the user actually fixes it instead of lingering
    // until the next blur/submit.
    if (touched[id]) {
      setErrors((prev) => ({ ...prev, [id]: validateField(id, value) }));
    }
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;
    setTouched((prev) => ({ ...prev, [id]: true }));
    setErrors((prev) => ({ ...prev, [id]: validateField(id, value) }));
  };

  const validateAll = () => {
    const nextErrors = Object.keys(initialFormData).reduce((acc, id) => {
      acc[id] = validateField(id, formData[id]);
      return acc;
    }, {});
    setErrors(nextErrors);
    setTouched(Object.keys(initialFormData).reduce((acc, id) => ({ ...acc, [id]: true }), {}));
    return Object.values(nextErrors).every((msg) => !msg);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Catch bad input here, before it ever leaves the browser — the whole
    // point being the user sees exactly what's wrong immediately, instead of
    // submitting, waiting on a round trip, and getting back a generic
    // "invalid data" error from the API.
    if (!validateAll()) {
      triggerToast("Please fix the highlighted fields before submitting.", "error");
      return;
    }

    setSending(true);

    try {
      await submitSidequestPreRegistration({
        name: formData.name.trim(),
        email: formData.email.trim(),
        key_collaboration: "Sidequest",
        company_name: formData.companyName.trim(),
        message: formData.message.trim(),
        social_media_link: formData.socialMediaLink.trim(),
      });

      trackEvent(ANALYTICS_EVENTS.CONVERSION.SIDEQUEST_PREREGISTERED);

      setSubmitted(true);
      setFormData(initialFormData);
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error("Error submitting Sidequest pre-registration:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to submit your request. Please try again.";
      triggerToast(errorMessage, "error");
    } finally {
      setSending(false);
    }
  };

  const fieldClass = (id) => `${styles.formInput}${errors[id] ? ` ${styles.formInputError}` : ""}`;

  return (
    <section className={`${styles.section} ${styles.registerSection}`} id="register-form">
      <div className={styles.container}>
        <div className={styles.registerGrid}>
          <motion.div
            className={styles.registerInfo}
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <span className={styles.badge}>Request Access</span>
            <h2>Ready to get started?</h2>
            <p>
              Fill in your details and we&apos;ll take care of the rest &mdash;
              your access is set up for you once verified.
            </p>

            <div className={styles.registerBullets}>
              {registerBullets.map((bullet) => (
                <div key={bullet} className={styles.registerBullet}>
                  <CheckCircle size={16} />
                  {bullet}
                </div>
              ))}
            </div>

            <div className={styles.registerNote}>
              <ShieldCheck size={18} />
              Reserved exclusively for Sidequest developers.
            </div>
          </motion.div>

          <motion.div
            className={styles.formModalWrap}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
          <span className={styles.formRibbon}>Partner Access</span>
          <div className={styles.formModal}>
          {submitted ? (
            <div className={styles.successState}>
              <div className={styles.successIcon}>
                <CheckCircle2 size={32} color="#34c38f" />
              </div>
              <h3 className={styles.successTitle}>Request Received</h3>
              <p className={styles.successText}>
                Thanks for pre-registering. We&apos;ll verify your details and
                grant access once approved &mdash; you&apos;ll hear from us
                shortly.
              </p>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setSubmitted(false)}
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            // noValidate: without it the browser's own HTML5 constraint
            // validation (from `required`/`type="email"`) intercepts the
            // submit before this component's onSubmit ever runs, so our
            // custom messages below never got a chance to render — the
            // user would see nothing, or an inconsistent native tooltip
            // depending on the browser.
            <Form onSubmit={handleSubmit} noValidate>
              <div className={styles.formGroup}>
                <Label className={styles.formLabel}>Name</Label>
                <Input
                  id="name"
                  className={fieldClass("name")}
                  placeholder="Enter your full name"
                  type="text"
                  required
                  invalid={Boolean(errors.name)}
                  aria-invalid={Boolean(errors.name)}
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                {errors.name && <span className={styles.formErrorText}>{errors.name}</span>}
              </div>

              <div className={styles.formGroup}>
                <Label className={styles.formLabel}>Work Email</Label>
                <Input
                  id="email"
                  className={fieldClass("email")}
                  placeholder="Enter your work email"
                  type="email"
                  required
                  invalid={Boolean(errors.email)}
                  aria-invalid={Boolean(errors.email)}
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                {errors.email && <span className={styles.formErrorText}>{errors.email}</span>}
              </div>

              <div className={styles.formGroup}>
                <Label className={styles.formLabel}>Company Name</Label>
                <Input
                  id="companyName"
                  className={styles.formInput}
                  placeholder="Optional"
                  type="text"
                  value={formData.companyName}
                  onChange={handleInputChange}
                />
              </div>

              <div className={styles.formGroup}>
                <Label className={styles.formLabel}>Social Media Link</Label>
                <Input
                  id="socialMediaLink"
                  className={fieldClass("socialMediaLink")}
                  placeholder="Discord, GitHub, or other social link, optional"
                  type="text"
                  invalid={Boolean(errors.socialMediaLink)}
                  aria-invalid={Boolean(errors.socialMediaLink)}
                  value={formData.socialMediaLink}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                {errors.socialMediaLink && (
                  <span className={styles.formErrorText}>{errors.socialMediaLink}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <Label className={styles.formLabel}>Tell Us About Your Project</Label>
                <Input
                  id="message"
                  className={styles.formInput}
                  placeholder="Optional"
                  type="textarea"
                  rows={3}
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </div>

              <button className={styles.submitBtn} type="submit" disabled={sending}>
                {sending ? <Spinner size="sm" color="white" /> : "Request Access"}
              </button>
            </Form>
          )}
          </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RegisterForm;
